import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ControlContainer,
  FormGroupDirective,
  FormGroupName,
  UntypedFormControl,
} from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';

import { getControl } from '../form-control-helper.utils';

export interface SelectComponentItem {
  text: string;
  value: string | number | unknown;
  /** Whether to always show this entry during a search */
  alwaysShowDuringSearch?: boolean;
}

@Component({
  selector: 'ui-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: [FormGroupDirective, FormGroupName],
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent implements OnInit, OnDestroy {
  @HostBinding('class.form-group') isBootstrapFormGroup = true;
  @HostBinding('class.d-block') isBlock = true;

  @Input() bindLabel = 'text';
  @Input() bindValue = 'value';
  @Input() clearable = false;
  @Input() control!: UntypedFormControl;
  @Input() controlName = '';
  @Input() domId = '';
  @Input() useTemplate = false;
  @Input() useGridDisplay?: boolean = false;

  @Input() label = '';
  @Input() labelColor: 'black' | 'white' = 'black';
  @Input() placeholder = 'Select';

  /**
   * CSS selector of the element the option panel should be rendered INTO, instead
   * of inline after the field (ng-select's `appendTo`).
   *
   * Needed when an ancestor SCROLLS or hides its overflow. The panel is
   * `position: absolute`, and a scroll container clips absolutely-positioned
   * descendants no matter how high their `z-index` is — so inside such an
   * ancestor the option list gets cut off at that ancestor's edge rather than
   * drawing over the content below it. Rendering it into `body` takes it out of
   * that clip; ng-select then positions it against the field's live
   * bounding box and keeps it in sync while open.
   *
   * OFF by default, because an appended panel no longer scrolls WITH its field —
   * only set it where the clipping is real (e.g. `appendTo="body"`).
   */
  @Input() appendTo?: string;
  @Input() searchable = false;
  @Input() items: unknown[] = [];
  @Input() invalid = false;
  @Output() handleChange = new EventEmitter();
  @Output() clearClicked = new EventEmitter();
  @Output() closeDropdown = new EventEmitter();

  /** The wrapped ng-select — backs `closePanel()`. */
  @ViewChild('select') private select?: NgSelectComponent;

  /** The scrolling ancestors currently being listened to (see below). */
  private scrollParents: HTMLElement[] = [];

  constructor(
    private readonly hostRef: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    @Optional() private parentForm: FormGroupDirective,
    @Optional() private groupName: FormGroupName
  ) {}

  ngOnInit(): void {
    if (!this.control) {
      this.control = getControl(
        'ui-select',
        this.parentForm,
        this.groupName,
        this.controlName
      ) as UntypedFormControl;
    }
    this.watchScrollAncestors();
  }

  ngOnDestroy(): void {
    this.unwatchScrollAncestors();
  }

  /** This custom search function performs the regular string search, as well
   * as allowing always-visible entries */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customSearchFn = (term: string, item: any) => {
    term = term.toLocaleLowerCase('en-US');
    return (
      item[this.bindLabel].toLocaleLowerCase('en-US').includes(term) ||
      item['alwaysShowDuringSearch']
    );
  };

  setSelectedValue(value: string) {
    this.control.setValue(value);
  }

  onChange($event: unknown) {
    this.handleChange.emit($event);
  }

  onClear($event: unknown) {
    this.clearClicked.emit($event);
  }

  onClose() {
    this.closeDropdown.emit();
  }

  /**
   * Dismiss the option panel if it is open. A no-op when already closed.
   */
  closePanel(): void {
    if (this.select?.isOpen) {
      this.select.close();
    }
  }

  /**
   * With `appendTo`, close the panel when a scrolling ancestor moves the field.
   *
   * An appended panel is positioned ONCE against the field's viewport rect and
   * is not re-positioned afterwards — ng-select listens to the panel's own
   * scroll, not to the field's ancestors — so scrolling the region the field
   * lives in would leave the option list hanging where the field used to be.
   * Closing is the standard resolution (Bootstrap and ng-bootstrap do the same
   * for their body-appended menus) and is far cheaper than tracking the rect.
   *
   * Registered only when appending, so the default inline behavior is untouched:
   * an inline panel is a sibling of the field and scrolls WITH it.
   */
  private watchScrollAncestors(): void {
    if (!this.appendTo) {
      return;
    }
    // Passive + outside Angular: a scroll listener must not schedule change
    // detection on every frame. `closePanel` only acts while the panel is open,
    // and ng-select re-enters Angular itself when it closes.
    this.zone.runOutsideAngular(() => {
      for (const el of this.scrollAncestors()) {
        el.addEventListener('scroll', this.onAncestorScroll, { passive: true });
        this.scrollParents.push(el);
      }
    });
  }

  private unwatchScrollAncestors(): void {
    for (const el of this.scrollParents) {
      el.removeEventListener('scroll', this.onAncestorScroll);
    }
    this.scrollParents = [];
  }

  /** Arrow property so add/removeEventListener see one stable reference. */
  private readonly onAncestorScroll = (): void => this.closePanel();

  /**
   * The ancestors that can scroll this field out from under its panel: every
   * element between the host and the document whose computed overflow is not
   * `visible`. Collected once, on init — the panel is only ever open for a
   * moment, so a live re-scan would buy nothing.
   */
  private scrollAncestors(): HTMLElement[] {
    const found: HTMLElement[] = [];
    let el = this.hostRef.nativeElement.parentElement;
    while (el && el !== document.body) {
      const { overflow, overflowX, overflowY } = getComputedStyle(el);
      if (
        /(auto|scroll|hidden|clip|overlay)/.test(
          overflow + overflowX + overflowY
        )
      ) {
        found.push(el);
      }
      el = el.parentElement;
    }
    return found;
  }
}
